import { toast } from "sonner";
import { motion } from "framer-motion";
import { Camera, Volume2, Activity, BarChart3, Bell } from "lucide-react";
import Reveal from "@/components/Reveal";

const FEATURES = [
  {
    icon: Camera,
    title: "Phone Camera Only",
    desc: "No extra hardware — prop your phone at the net post and it watches the whole court.",
    id: "camera",
  },
  {
    icon: Volume2,
    title: "Spoken Calls",
    desc: "Every ball called IN or OUT, out loud, the instant it bounces.",
    id: "calls",
  },
  {
    icon: Activity,
    title: "Live Rally Tracking",
    desc: "Follows the ball bounce-by-bounce through every rally, at full match speed.",
    id: "tracking",
  },
  {
    icon: BarChart3,
    title: "Scores & Stats",
    desc: "Keeps score automatically and hands you a full match summary at the end.",
    id: "stats",
  },
];

export const CourtGraphic = () => (
  <div className="relative mx-auto w-full max-w-sm" data-testid="court-vision-graphic">
    <div className="absolute -inset-8 rounded-full bg-sky-400/10 blur-3xl -z-10" aria-hidden="true" />
    <svg
      viewBox="0 0 320 400"
      className="w-full h-auto rounded-2xl border border-line shadow-2xl"
      role="img"
      aria-label="Stylised tennis court with a ball called out"
    >
      <rect width="320" height="400" fill="#14532D" />
      <rect x="40" y="24" width="240" height="352" fill="none" stroke="#F8FAFC" strokeWidth="3" />
      <line x1="73" y1="24" x2="73" y2="376" stroke="#F8FAFC" strokeWidth="2.5" />
      <line x1="247" y1="24" x2="247" y2="376" stroke="#F8FAFC" strokeWidth="2.5" />
      <line x1="73" y1="112" x2="247" y2="112" stroke="#F8FAFC" strokeWidth="2.5" />
      <line x1="73" y1="288" x2="247" y2="288" stroke="#F8FAFC" strokeWidth="2.5" />
      <line x1="160" y1="112" x2="160" y2="288" stroke="#F8FAFC" strokeWidth="2.5" />
      <line x1="32" y1="200" x2="288" y2="200" stroke="#F8FAFC" strokeWidth="5" />
      <line x1="160" y1="24" x2="160" y2="40" stroke="#F8FAFC" strokeWidth="2.5" />
      <line x1="160" y1="360" x2="160" y2="376" stroke="#F8FAFC" strokeWidth="2.5" />
      <circle cx="268" cy="98" r="8" fill="#D9F99D" stroke="#84CC16" strokeWidth="2" />
    </svg>
    <motion.span
      data-testid="court-vision-call-badge"
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: [0, 1, 1, 0], scale: [0.6, 1.05, 1, 0.9] }}
      transition={{ duration: 3.2, repeat: Infinity, times: [0, 0.2, 0.8, 1] }}
      className="absolute left-[72%] top-[16%] rounded-lg bg-crimson px-3.5 py-1.5 font-display text-2xl tracking-widest text-white shadow-lg"
    >
      OUT
    </motion.span>
  </div>
);

export default function CourtVision() {
  return (
    <div data-testid="court-vision-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden grain pt-32 pb-20">
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-br from-sky-500/10 via-pitch to-pitch"
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="text-xs font-mono uppercase tracking-[0.3em] text-sky-400 mb-6"
              data-testid="court-vision-eyebrow"
            >
              Court Vision · Automatic Tennis Line Calling
            </motion.p>
            <h1
              className="font-display leading-[0.88] tracking-tight text-[clamp(4rem,11vw,8.5rem)]"
              data-testid="court-vision-heading"
            >
              <span className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                >
                  EVERY CALL.
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span
                  className="block text-sky-400 drop-shadow-[0_0_24px_rgba(56,189,248,0.35)]"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  NO ARGUMENTS.
                </motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-7 max-w-md text-base sm:text-lg text-slate-300 leading-relaxed"
              data-testid="court-vision-subcopy"
            >
              Automatic line calling for tennis, straight from your phone. Court
              Vision watches every rally and calls each ball IN or OUT in real
              time — so every friendly match finally has an umpire.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-9"
            >
              <button
                data-testid="court-vision-cta"
                onClick={() => toast.info("Court Vision is coming soon — stay tuned!")}
                className="inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-3.5 font-bold text-pitch transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <Bell size={16} strokeWidth={2.5} />
                Coming soon
              </button>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-6 text-xs font-mono uppercase tracking-widest text-slate-500"
            >
              iOS & Android · Works on any court · No extra hardware
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <CourtGraphic />
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section data-testid="court-vision-features" className="py-24 sm:py-32 bg-turf/30 border-y border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-sky-400 mb-4">
              What's in the app
            </p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none">
              YOUR POCKET <span className="text-sky-400">UMPIRE</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.08}>
                <motion.div
                  data-testid={`court-vision-feature-card-${f.id}`}
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  className="h-full rounded-2xl border border-line bg-turf/70 p-8 hover:border-sky-400/40 transition-colors duration-300"
                >
                  <span className="inline-flex w-12 h-12 rounded-xl bg-sky-400/10 border border-sky-400/25 items-center justify-center text-sky-400">
                    <f.icon size={22} />
                  </span>
                  <h3 className="mt-5 font-head text-xl sm:text-2xl font-bold">{f.title}</h3>
                  <p className="mt-2.5 text-sm sm:text-base text-slate-400 leading-relaxed">
                    {f.desc}
                  </p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="court-vision-cta-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-sky-400/25 bg-gradient-to-br from-turf via-pitch to-turf p-10 sm:p-16 text-center grain">
              <div
                className="absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-sky-400/10 blur-3xl"
                aria-hidden="true"
              />
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-sky-400">
                In warm-up now
              </p>
              <h2 className="mt-4 font-display text-5xl sm:text-7xl tracking-tight leading-none">
                READY FOR <span className="text-sky-400">MATCH POINT?</span>
              </h2>
              <p className="mt-5 max-w-lg mx-auto text-slate-300">
                Court Vision is heading to the App Store and Google Play. Check
                back here on launch day.
              </p>
              <button
                data-testid="court-vision-cta-bottom"
                onClick={() => toast.info("Court Vision is coming soon — stay tuned!")}
                className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-sky-400 px-7 py-3.5 font-bold text-pitch transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <Bell size={16} strokeWidth={2.5} />
                Coming soon
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
